import React, { useState } from "react";

function money(amount) {
  const value = Number(amount) || 0;
  return `₦${value.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pdfMoney(amount) {
  return money(amount).replace("₦", "NGN ");
}

async function downloadReceiptPdf({ title, receipt, amount }) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "pt", format: "a5" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const paidAt = receipt.paidAt ? new Date(receipt.paidAt) : new Date();
  const heading = title || "Payment receipt";

  doc.setFillColor(22, 101, 52);
  doc.rect(0, 0, pageWidth, 96, "F");
  doc.setTextColor(220, 252, 231);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("JHONKS", 28, 32);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.text(heading, 28, 56);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text("Paid", 28, 76);

  const rows = [
    ["Receipt", receipt.reference || "—"],
    ["Date", paidAt.toLocaleString()],
    ["Amount", pdfMoney(receipt.amount ?? amount)],
    ["Account name", receipt.accountName || "—"],
    ["Account number", receipt.accountNumber || "—"],
    ["Bank", receipt.bankName || "—"],
    ["Method", receipt.method === "manual" ? "Manual transfer" : "Paystack"],
  ];

  let y = 128;
  rows.forEach(([label, value]) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(107, 114, 128);
    doc.text(label, 28, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(17, 24, 39);
    const lines = doc.splitTextToSize(String(value), pageWidth - 160);
    doc.text(lines, pageWidth - 28, y, { align: "right" });
    y += 26 + (lines.length - 1) * 14;
  });

  doc.setDrawColor(229, 231, 235);
  doc.line(28, y, pageWidth - 28, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  doc.text("This receipt confirms a Jhonks payment.", 28, y + 22);

  const safe = String(receipt.reference || "receipt").replace(/[^\w.-]+/g, "-");
  doc.save(`jhonks-${safe}.pdf`);
}

const PaymentReceipt = ({ title, receipt, amount, onClose }) => {
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");
  if (!receipt) return null;
  const paidAt = receipt.paidAt ? new Date(receipt.paidAt) : new Date();

  const printReceipt = async () => {
    if (printing) return;
    setPrinting(true);
    setError("");
    try {
      await downloadReceiptPdf({ title, receipt, amount });
    } catch (printError) {
      setError("The receipt could not be saved. Please try again.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <article
        className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="bg-green-800 px-6 py-5 text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-green-100">Jhonks</p>
          <h2 className="mt-1 text-2xl font-bold">{title || "Payment receipt"}</h2>
          <p className="mt-1 text-sm text-green-50">Paid</p>
        </div>
        <dl className="space-y-3 px-6 py-5 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Receipt</dt>
            <dd className="font-semibold text-gray-900">{receipt.reference}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Date</dt>
            <dd className="text-right text-gray-900">{paidAt.toLocaleString()}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Amount</dt>
            <dd className="text-lg font-bold text-green-700">{money(receipt.amount ?? amount)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Account name</dt>
            <dd className="text-right text-gray-900">{receipt.accountName || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Account number</dt>
            <dd className="text-gray-900">{receipt.accountNumber || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Bank</dt>
            <dd className="text-right text-gray-900">{receipt.bankName || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Method</dt>
            <dd className="text-gray-900">{receipt.method === "manual" ? "Manual transfer" : "Paystack"}</dd>
          </div>
        </dl>
        {error && <p className="px-6 pb-2 text-sm text-red-700">{error}</p>}
        <div className="flex gap-2 border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-300 py-3 text-sm font-semibold text-gray-700"
          >
            Close
          </button>
          <button
            type="button"
            onClick={printReceipt}
            disabled={printing}
            className="flex-1 rounded-xl bg-green-800 py-3 text-sm font-semibold text-white disabled:opacity-60"
          >
            {printing ? "Preparing…" : "Print"}
          </button>
        </div>
      </article>
    </div>
  );
};

export default PaymentReceipt;
